import { graphql, useStaticQuery } from "gatsby"
import Img from "gatsby-image"
import React from "react"
import { css } from "styled-components"
import { Block, Grid, Spacer } from "../../atoms/layout-primitives"
import ContentBlock from "../../molecules/content-block"
import UsecaseCards from "../../organisms/usecase-cards"

export default function UseCases() {
  const query = useStaticQuery(graphql`
    query {
      abstract: file(
        relativePath: {
          eq: "1_SOLUTIONS/Mapping CMS - Internal Pages/Indoor Positioning/use cases/abstract blocks.png"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 1920, quality: 100) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      wayfindingImage: file(
        relativePath: {
          eq: "1_SOLUTIONS/Mapping CMS - Internal Pages/Indoor Positioning/use cases/tablet/use case 01.jpg"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 1920, quality: 100) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      trackingImage: file(
        relativePath: {
          eq: "1_SOLUTIONS/Mapping CMS - Internal Pages/Indoor Positioning/use cases/tablet/use case 02.jpg"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 1920, quality: 100) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      monitoringImage: file(
        relativePath: {
          eq: "1_SOLUTIONS/Mapping CMS - Internal Pages/Indoor Positioning/use cases/tablet/use case 03.jpg"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 1920, quality: 100) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      applicationsImage: file(
        relativePath: {
          eq: "1_SOLUTIONS/Mapping CMS - Internal Pages/Indoor Positioning/use cases/tablet/use case 04.jpg"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 1920, quality: 100) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      wayfindingDesktop: file(
        relativePath: {
          eq: "1_SOLUTIONS/Mapping CMS - Internal Pages/Indoor Positioning/use cases/desktop/use case 01.jpg"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 1920, quality: 100) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      trackingDesktop: file(
        relativePath: {
          eq: "1_SOLUTIONS/Mapping CMS - Internal Pages/Indoor Positioning/use cases/desktop/use case 02.jpg"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 1920, quality: 100) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      monitoringDesktop: file(
        relativePath: {
          eq: "1_SOLUTIONS/Mapping CMS - Internal Pages/Indoor Positioning/use cases/desktop/use case 03.jpg"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 1920, quality: 100) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      applicationsDesktop: file(
        relativePath: {
          eq: "1_SOLUTIONS/Mapping CMS - Internal Pages/Indoor Positioning/use cases/desktop/use case 04.jpg"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 1920, quality: 100) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
    }
  `)

  const Cards = [
    {
      title: "Intuitive Wayfinding",
      body: 'With IPS, users can see their precise location represented by a blue dot within the context of an indoor map. Similar to outdoor GPS, users are not required to enter a "start" destination and as they begin to follow along a route, the blue dot moves with them, providing an enhanced navigational experience.',
      image: query.wayfindingDesktop.childImageSharp,
    },
    {
      title: "Asset Tracking",
      body: "Indoor spaces such as hospitals, warehouses, offices, and even buildings under construction can leverage IPS to track important assets. Documents, employees, building material, and equipment can be visualized in real-time on a map. Indoor location tracking and positioning can significantly increase efficiencies and reduce errors in these spaces.",
      image: query.trackingDesktop.childImageSharp,
    },
    {
      title: "Contact Monitoring",
      body: "Indoor positioning solutions allow businesses to pull an accurate location of employees in real-time, which can be combined with spatial and temporal analysis to identify potential contact events. These solutions are helping to make indoor spaces safer.",
      image: query.monitoringDesktop.childImageSharp,
    },
    {
      title: "Smart Building Applications",
      body: "By enabling wayfinding, asset tracking, and contact monitoring through IPS, buildings are able to create a network of connected devices and experiences. Together, these pieces allow buildings to get a true picture of their venue and expose areas of improvement.",
      image: query.applicationsDesktop.childImageSharp,
    },
  ]

  return (
    <>
      <Grid>
        <Spacer height={{ sm: "24px", md: "16px", lg: "32px" }} />
        <Block
          col={{ sm: "2 / -2", md: "4 / -4", lg: "5 / 9" }}
          variants={[
            css`
              z-index: 1;
            `,
          ]}
        >
          <Img
            fluid={query.abstract.childImageSharp.fluid}
            alt="abstract blocks image"
          />
        </Block>
        <Block
          col={{ sm: "1 / -1", md: "2 / -2", lg: "3 / -3" }}
          variants={[
            css`
              text-align: center;
              z-index: 2;
            `,
          ]}
        >
          <ContentBlock.H2
            title={
              <>
                Indoor Positioning <span>Use Cases</span>
              </>
            }
            description="Using accurate positioning in an indoor environment allows businesses to provide new efficiencies and experiences, such as: "
            variants={[
              css`
                margin-top: -64px;
              `,
              css`
                margin-top: -72px;
              `,
              css`
                margin-top: -72px;
              `,
            ]}
            titleVariants={[
              css`
                font-weight: 400;
                & > span {
                  font-weight: 500;
                  color: var(--burnt-orange);
                }
              `,
              css`
                font-weight: 400;
              `,
              css`
                font-weight: 400;
              `,
            ]}
          />
        </Block>
        <Spacer height={{ sm: "48px", md: "48px", lg: "64px" }} />
      </Grid>
      <UsecaseCards useCases={Cards} />
      <Grid>
        <Spacer
          height={{ sm: "72px", md: "88px", lg: "64px" }} // added additional 40px to account for slider for mobile and tablet viewports
        />
      </Grid>
    </>
  )
}
